// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import * as tslib_1 from "tslib";
import { RequestHeaders, Util, CorrelationIdHelper, TelemetryItemCreator, RemoteDependencyData, DateTimeUtils, DisabledPropertyName, PropertiesPluginIdentifier, DistributedTracingModes } from '@microsoft/applicationinsights-common';
import { CoreUtils, LoggingSeverity, _InternalMessageId, BaseTelemetryPlugin, getLocation, getGlobal } from '@microsoft/applicationinsights-core-js';
import { ajaxRecord } from './ajaxRecord';
import { EventHelper } from './ajaxUtils';
import { Traceparent } from './TraceParent';
var _isNullOrUndefined = CoreUtils.isNullOrUndefined;
var AjaxMonitor = /** @class */ (function (_super) {
    tslib_1.__extends(AjaxMonitor, _super);
    function AjaxMonitor() {
        var _this = _super.call(this) || this;
        _this.identifier = AjaxMonitor.identifier;
        _this.priority = 120;
        _this._config = AjaxMonitor.getEmptyConfig();
        _this._trackAjaxAttempts = 0;
        var location = getLocation();
        _this.currentWindowHost = location && location.host && location.host.toLowerCase();
        _this.initialized = false;
        _this._fetchInitialized = false;
        return _this;
    }
    AjaxMonitor.getDefaultConfig = function () {
        var config = {
            maxAjaxCallsPerView: 500,
            disableAjaxTracking: false,
            disableFetchTracking: true,
            disableCorrelationHeaders: false,
            distributedTracingMode: DistributedTracingModes.AI,
            correlationHeaderExcludedDomains: [
                "*.blob.core.windows.net",
                "*.blob.core.chinacloudapi.cn",
                "*.blob.core.cloudapi.de",
                "*.blob.core.usgovcloudapi.net"
            ],
            correlationHeaderDomains: undefined,
            appId: undefined,
            enableCorsCorrelation: false,
            enableRequestHeaderTracking: false,
            enableResponseHeaderTracking: false,
            enableAjaxErrorStatusText: false
        };
        return config;
    };
    AjaxMonitor.getEmptyConfig = function () {
        return {
            maxAjaxCallsPerView: undefined,
            disableAjaxTracking: undefined,
            disableFetchTracking: undefined,
            disableCorrelationHeaders: undefined,
            distributedTracingMode: undefined,
            correlationHeaderExcludedDomains: undefined,
            appId: undefined,
            enableCorsCorrelation: undefined,
            correlationHeaderDomains: undefined,
            enableRequestHeaderTracking: undefined,
            enableResponseHeaderTracking: undefined,
            enableAjaxErrorStatusText: undefined
        };
    };
    AjaxMonitor.getFailedAjaxDiagnosticsMessage = function (xhr) {
        var result = "";
        try {
            if (!_isNullOrUndefined(xhr) &&
                !_isNullOrUndefined(xhr.ajaxData) &&
                !_isNullOrUndefined(xhr.ajaxData.requestUrl)) {
                result += "(url: '" + xhr.ajaxData.requestUrl + "')";
            }
        }
        catch (e) { }
        return result;
    };
    AjaxMonitor.prototype.trackDependencyData = function (dependency, properties) {
        this.trackDependencyDataInternal(dependency, properties);
    };
    AjaxMonitor.prototype.processTelemetry = function (item, itemCtx) {
        this.processNext(item, itemCtx);
    };
    AjaxMonitor.prototype.includeCorrelationHeaders = function (ajaxData, input, init, xhr) {
        var _self = this;
        if (input) {
            if (CorrelationIdHelper.canIncludeCorrelationHeader(_self._config, ajaxData.getAbsoluteUrl(), _self.currentWindowHost)) {
                if (!init) {
                    init = {};
                }
                // init headers override original request headers
                // so, if they exist use only them, otherwise use request's because they should have been applied in the first place
                // not using original request headers will result in them being lost
                init.headers = new Headers(init.headers || (input instanceof Request ? (input.headers || {}) : {}));
                if (_self._isUsingAIHeaders) {
                    var id = "|" + ajaxData.traceID + "." + ajaxData.spanID;
                    init.headers.set(RequestHeaders.requestIdHeader, id);
                    if (_self._config.enableRequestHeaderTracking) {
                        ajaxData.requestHeaders[RequestHeaders.requestIdHeader] = id;
                    }
                }
                var appId = _self._config.appId || (_self._context && _self._context.appId());
                if (appId) {
                    init.headers.set(RequestHeaders.requestContextHeader, RequestHeaders.requestContextAppIdFormat + appId);
                    if (_self._config.enableRequestHeaderTracking) {
                        ajaxData.requestHeaders[RequestHeaders.requestContextHeader] = RequestHeaders.requestContextAppIdFormat + appId;
                    }
                }
                if (_self._isUsingW3CHeaders) {
                    var traceparent = new Traceparent(ajaxData.traceID, ajaxData.spanID);
                    init.headers.set(RequestHeaders.traceParentHeader, traceparent.toString());
                    if (_self._config.enableRequestHeaderTracking) {
                        ajaxData.requestHeaders[RequestHeaders.traceParentHeader] = traceparent.toString();
                    }
                }
                return init;
            }
            return init;
        }
        else if (xhr) {
            if (CorrelationIdHelper.canIncludeCorrelationHeader(_self._config, xhr.ajaxData.getAbsoluteUrl(), _self.currentWindowHost)) {
                if (_self._isUsingAIHeaders) {
                    var id = "|" + xhr.ajaxData.traceID + "." + xhr.ajaxData.spanID;
                    xhr.setRequestHeader(RequestHeaders.requestIdHeader, id);
                    if (_self._config.enableRequestHeaderTracking) {
                        xhr.ajaxData.requestHeaders[RequestHeaders.requestIdHeader] = id;
                    }
                }
                var appId = _self._config.appId || (_self._context && _self._context.appId());
                if (appId) {
                    xhr.setRequestHeader(RequestHeaders.requestContextHeader, RequestHeaders.requestContextAppIdFormat + appId);
                    if (_self._config.enableRequestHeaderTracking) {
                        xhr.ajaxData.requestHeaders[RequestHeaders.requestContextHeader] = RequestHeaders.requestContextAppIdFormat + appId;
                    }
                }
                if (_self._isUsingW3CHeaders) {
                    var traceparent = new Traceparent(xhr.ajaxData.traceID, xhr.ajaxData.spanID);
                    xhr.setRequestHeader(RequestHeaders.traceParentHeader, traceparent.toString());
                    if (_self._config.enableRequestHeaderTracking) {
                        xhr.ajaxData.requestHeaders[RequestHeaders.traceParentHeader] = traceparent.toString();
                    }
                }
            }
            return xhr;
        }
        return undefined;
    };
    AjaxMonitor.prototype.initialize = function (config, core, extensions, pluginChain) {
        var _self = this;
        _super.prototype.initialize.call(this, config, core, extensions, pluginChain);
        var ctx = _self._getTelCtx();
        if (!_self.initialized && !_self._fetchInitialized) {
            var defaultConfig = AjaxMonitor.getDefaultConfig();
            for (var field in defaultConfig) {
                _self._config[field] = ctx.getConfig(AjaxMonitor.identifier, field, defaultConfig[field]);
            }
            _self._isUsingAIHeaders = _self._config.distributedTracingMode === DistributedTracingModes.AI || _self._config.distributedTracingMode === DistributedTracingModes.AI_AND_W3C;
            _self._isUsingW3CHeaders = _self._config.distributedTracingMode === DistributedTracingModes.AI_AND_W3C || _self._config.distributedTracingMode === DistributedTracingModes.W3C;
            if (_self._config.disableAjaxTracking === false) {
                _self.instrumentXhr();
            }
            if (_self._config.disableFetchTracking === false) {
                _self.instrumentFetch();
            }
            if (extensions.length > 0 && extensions) {
                var propExt = void 0, extIx = 0;
                while (!propExt && extIx < extensions.length) {
                    if (extensions[extIx] && extensions[extIx].identifier === PropertiesPluginIdentifier) {
                        propExt = extensions[extIx];
                    }
                    extIx++;
                }
                if (propExt) {
                    _self._context = propExt.context; // we could move IPropertiesPlugin to common as well
                }
            }
        }
    };
    /**
     * Logs dependency call
     * @param dependencyData dependency data object
     */
    AjaxMonitor.prototype.trackDependencyDataInternal = function (dependency, properties, systemProperties) {
        var _self = this;
        if (_self._config.maxAjaxCallsPerView === -1 || _self._trackAjaxAttempts < _self._config.maxAjaxCallsPerView) {
            var item = TelemetryItemCreator.create(dependency, RemoteDependencyData.dataType, RemoteDependencyData.envelopeType, _self.diagLog(), properties, systemProperties);
            _self.core.track(item);
        }
        else if (_self._trackAjaxAttempts === _self._config.maxAjaxCallsPerView) {
            _self.diagLog().throwInternal(LoggingSeverity.CRITICAL, _InternalMessageId.MaxAjaxPerPVExceeded, "Maximum ajax per page view limit reached, ajax monitoring is paused until the next trackPageView(). In order to increase the limit set the maxAjaxCallsPerView configuration parameter.", true);
        }
        ++_self._trackAjaxAttempts;
    };
    // Fetch Stuff
    AjaxMonitor.prototype.instrumentFetch = function () {
        var _self = this;
        if (!_self.supportsFetch() || _self._fetchInitialized) {
            return;
        }
        // Getting the global instance tp support web workers (which don't have window)
        var global = getGlobal();
        var originalFetch = global.fetch;
        var fetchMonitorInstance = _self;
        global.fetch = function fetch(input, init) {
            var fetchData;
            if (fetchMonitorInstance.isFetchInstrumented(input) && fetchMonitorInstance.isMonitoredInstance(undefined, undefined, input, init)) {
                try {
                    fetchData = fetchMonitorInstance.createFetchRecord(input, init);
                    init = fetchMonitorInstance.includeCorrelationHeaders(fetchData, input, init);
                }
                catch (e) {
                    fetchMonitorInstance.diagLog().throwInternal(LoggingSeverity.CRITICAL, _InternalMessageId.FailedMonitorAjaxOpen, "Failed to monitor Window.fetch, monitoring data for this fetch call may be incorrect.", {
                        ajaxDiagnosticsMessage: _self.getFailedFetchDiagnosticsMessage(input),
                        exception: Util.dump(e)
                    });
                }
            }
            return originalFetch(input, init)
                .then(function (response) {
                fetchMonitorInstance.onFetchComplete(response, fetchData);
                return response;
            })["catch"](function (reason) {
                fetchMonitorInstance.onFetchFailed(input, fetchData, reason);
                throw reason;
            });
        };
        _self._fetchInitialized = true;
    };
    AjaxMonitor.prototype.instrumentXhr = function () {
        var _self = this;
        if (_self.supportsAjaxMonitoring() && !_self.initialized) {
            _self.instrumentOpen();
            _self.instrumentSend();
            _self.instrumentAbort();
            _self.instrumentSetRequestHeader();
            _self.initialized = true;
        }
    };
    /// <summary>Verifies that particalar instance of XMLHttpRequest needs to be monitored</summary>
    /// <param name="excludeAjaxDataValidation">Optional parameter. True if ajaxData must be excluded from verification</param>
    /// <returns type="bool">True if instance needs to be monitored, otherwise false</returns>
    AjaxMonitor.prototype.isMonitoredInstance = function (xhr, excludeAjaxDataValidation, request, init) {
        var disabledProperty = false;
        var ajaxValidation = true;
        var initialized = false;
        if (typeof request !== 'undefined') {
            initialized = this._fetchInitialized;
            // Look for DisabledPropertyName in either Request or RequestInit
            disabledProperty = (typeof request === 'object' ? request[DisabledPropertyName] === true : false) ||
                (init ? init[DisabledPropertyName] === true : false);
        }
        else if (typeof xhr !== 'undefined') {
            initialized = this.initialized;
            disabledProperty = xhr[DisabledPropertyName] === true;
            ajaxValidation = excludeAjaxDataValidation === true || !_isNullOrUndefined(xhr.ajaxData);
        }
        // checking to see that all interested functions on xhr were instrumented
        return initialized
            // checking on ajaxData to see that it was not removed in user code
            && ajaxValidation
            // check that this instance is not not used by ajax call performed inside client side monitoring to send data to collector
            && !disabledProperty;
    };
    /// <summary>Determines whether ajax monitoring can be enabled on this document</summary>
    /// <returns>True if Ajax monitoring is supported on this page, otherwise false</returns>
    AjaxMonitor.prototype.supportsAjaxMonitoring = function () {
        var result = true;
        if (typeof XMLHttpRequest === 'undefined' ||
            _isNullOrUndefined(XMLHttpRequest) ||
            _isNullOrUndefined(XMLHttpRequest.prototype) ||
            _isNullOrUndefined(XMLHttpRequest.prototype.open) ||
            _isNullOrUndefined(XMLHttpRequest.prototype.send) ||
            _isNullOrUndefined(XMLHttpRequest.prototype.abort)) {
            result = false;
        }
        // disable in IE8 or older (https://www.w3schools.com/jsref/jsref_trim_string.asp)
        try {
            " a ".trim();
        }
        catch (ex) {
            result = false;
        }
        return result;
    };
    AjaxMonitor.prototype.instrumentOpen = function () {
        var originalOpen = XMLHttpRequest.prototype.open;
        var ajaxMonitorInstance = this;
        XMLHttpRequest.prototype.open = function (method, url, async) {
            var funcThis = this;
            try {
                if (ajaxMonitorInstance.isMonitoredInstance(funcThis, true) &&
                    (!funcThis.ajaxData ||
                        !funcThis.ajaxData.xhrMonitoringState.openDone)) {
                    ajaxMonitorInstance.openHandler(funcThis, method, url, async);
                }
            }
            catch (e) {
                ajaxMonitorInstance.diagLog().throwInternal(LoggingSeverity.CRITICAL, _InternalMessageId.FailedMonitorAjaxOpen, "Failed to monitor XMLHttpRequest.open, monitoring data for this ajax call may be incorrect.", {
                    ajaxDiagnosticsMessage: AjaxMonitor.getFailedAjaxDiagnosticsMessage(funcThis),
                    exception: Util.dump(e)
                });
            }
            return originalOpen.apply(funcThis, arguments);
        };
    };
    AjaxMonitor.prototype.openHandler = function (xhr, method, url, async) {
        var _self = this;
        var traceID = (_self._context && _self._context.telemetryTrace && _self._context.telemetryTrace.traceID) || Util.generateW3CId();
        var spanID = Util.generateW3CId().substr(0, 16);
        var ajaxData = new ajaxRecord(traceID, spanID, _self.diagLog());
        ajaxData.method = method;
        ajaxData.requestUrl = url;
        ajaxData.xhrMonitoringState.openDone = true;
        ajaxData.requestHeaders = {};
        xhr.ajaxData = ajaxData;
        _self.attachToOnReadyStateChange(xhr);
    };
    AjaxMonitor.prototype.instrumentSend = function () {
        var originalSend = XMLHttpRequest.prototype.send;
        var ajaxMonitorInstance = this;
        XMLHttpRequest.prototype.send = function (content) {
            var funcThis = this;
            try {
                if (ajaxMonitorInstance.isMonitoredInstance(funcThis) && !funcThis.ajaxData.xhrMonitoringState.sendDone) {
                    ajaxMonitorInstance.sendHandler(funcThis, content);
                }
            }
            catch (e) {
                ajaxMonitorInstance.diagLog().throwInternal(LoggingSeverity.CRITICAL, _InternalMessageId.FailedMonitorAjaxSend, "Failed to monitor XMLHttpRequest, monitoring data for this ajax call may be incorrect.", {
                    ajaxDiagnosticsMessage: AjaxMonitor.getFailedAjaxDiagnosticsMessage(funcThis),
                    exception: Util.dump(e)
                });
            }
            return originalSend.apply(funcThis, arguments);
        };
    };
    AjaxMonitor.prototype.sendHandler = function (xhr, content) {
        xhr.ajaxData.requestSentTime = DateTimeUtils.Now();
        xhr = this.includeCorrelationHeaders(xhr.ajaxData, undefined, undefined, xhr);
        xhr.ajaxData.xhrMonitoringState.sendDone = true;
    };
    AjaxMonitor.prototype.instrumentAbort = function () {
        var originalAbort = XMLHttpRequest.prototype.abort;
        var ajaxMonitorInstance = this;
        XMLHttpRequest.prototype.abort = function () {
            var funcThis = this;
            try {
                if (ajaxMonitorInstance.isMonitoredInstance(funcThis) && !funcThis.ajaxData.xhrMonitoringState.abortDone) {
                    funcThis.ajaxData.aborted = 1;
                    funcThis.ajaxData.xhrMonitoringState.abortDone = true;
                }
            }
            catch (e) {
                ajaxMonitorInstance.diagLog().throwInternal(LoggingSeverity.CRITICAL, _InternalMessageId.FailedMonitorAjaxAbort, "Failed to monitor XMLHttpRequest.abort, monitoring data for this ajax call may be incorrect.", {
                    ajaxDiagnosticsMessage: AjaxMonitor.getFailedAjaxDiagnosticsMessage(funcThis),
                    exception: Util.dump(e)
                });
            }
            return originalAbort.apply(funcThis, arguments);
        };
    };
    AjaxMonitor.prototype.instrumentSetRequestHeader = function () {
        if (!this._config.enableRequestHeaderTracking) {
            return;
        }
        var originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;
        var ajaxMonitorInstance = this;
        XMLHttpRequest.prototype.setRequestHeader = function (header, value) {
            var funcThis = this;
            try {
                if (ajaxMonitorInstance.isMonitoredInstance(funcThis)) {
                    funcThis.ajaxData.requestHeaders[header] = value;
                }
            }
            catch (e) {
                ajaxMonitorInstance.diagLog().throwInternal(LoggingSeverity.CRITICAL, _InternalMessageId.FailedMonitorAjaxSetRequestHeader, "Failed to monitor XMLHttpRequest.setRequestHeader, monitoring data for this ajax call may be incorrect.", {
                    ajaxDiagnosticsMessage: AjaxMonitor.getFailedAjaxDiagnosticsMessage(funcThis),
                    exception: Util.dump(e)
                });
            }
            return originalSetRequestHeader.apply(funcThis, arguments);
        };
    };
    AjaxMonitor.prototype.attachToOnReadyStateChange = function (xhr) {
        var ajaxMonitorInstance = this;
        xhr.ajaxData.xhrMonitoringState.onreadystatechangeCallbackAttached = EventHelper.Attach(xhr, "readystatechange", function () {
            try {
                if (ajaxMonitorInstance.isMonitoredInstance(xhr)) {
                    if (xhr.readyState === 4) {
                        ajaxMonitorInstance.onAjaxComplete(xhr);
                    }
                }
            }
            catch (e) {
                var exceptionText = Util.dump(e);
                // ignore messages with c00c023f, as this a known IE9 XHR abort issue
                if (!exceptionText || exceptionText.toLowerCase().indexOf("c00c023f") === -1) {
                    ajaxMonitorInstance.diagLog().throwInternal(LoggingSeverity.CRITICAL, _InternalMessageId.FailedMonitorAjaxRSC, "Failed to monitor XMLHttpRequest 'readystatechange' event handler, monitoring data for this ajax call may be incorrect.", {
                        ajaxDiagnosticsMessage: AjaxMonitor.getFailedAjaxDiagnosticsMessage(xhr),
                        exception: Util.dump(e)
                    });
                }
            }
        });
    };
    AjaxMonitor.prototype.onAjaxComplete = function (xhr) {
        var _self = this;
        xhr.ajaxData.responseFinishedTime = DateTimeUtils.Now();
        xhr.ajaxData.status = xhr.status;
        xhr.ajaxData.CalculateMetrics();
        if (xhr.ajaxData.ajaxTotalDuration < 0) {
            _self.diagLog().throwInternal(LoggingSeverity.WARNING, _InternalMessageId.FailedMonitorAjaxDur, "Failed to calculate the duration of the ajax call, monitoring data for this ajax call won't be sent.", {
                ajaxDiagnosticsMessage: AjaxMonitor.getFailedAjaxDiagnosticsMessage(xhr),
                requestSentTime: xhr.ajaxData.requestSentTime,
                responseFinishedTime: xhr.ajaxData.responseFinishedTime
            });
        }
        else {
            var dependency = {
                id: "|" + xhr.ajaxData.traceID + "." + xhr.ajaxData.spanID,
                target: xhr.ajaxData.getAbsoluteUrl(),
                name: xhr.ajaxData.getPathName(),
                type: "Ajax",
                duration: xhr.ajaxData.ajaxTotalDuration,
                success: (+(xhr.ajaxData.status)) >= 200 && (+(xhr.ajaxData.status)) < 400,
                responseCode: +xhr.ajaxData.status,
                method: xhr.ajaxData.method
            };
            // enrich dependency target with correlation context from the server
            var correlationContext = _self.getAjaxCorrelationContext(xhr);
            if (correlationContext) {
                dependency.correlationContext = /* dependency.target + " | " + */ correlationContext;
            }
            if (_self._config.enableRequestHeaderTracking) {
                if (CoreUtils.objKeys(xhr.ajaxData.requestHeaders).length > 0) {
                    dependency.properties = dependency.properties || {};
                    dependency.properties.requestHeaders = {};
                    dependency.properties.requestHeaders = xhr.ajaxData.requestHeaders;
                }
            }
            if (_self._config.enableResponseHeaderTracking) {
                var headers = xhr.getAllResponseHeaders();
                if (headers) {
                    // xhr.getAllResponseHeaders() method returns all the response headers, separated by CRLF, as a string or null
                    // the regex converts the header string into an array of individual headers
                    var arr = headers.trim().split(/[\r\n]+/);
                    var responseHeaderMap_1 = {};
                    CoreUtils.arrForEach(arr, function (line) {
                        var parts = line.split(': ');
                        var header = parts.shift();
                        var value = parts.join(': ');
                        responseHeaderMap_1[header] = value;
                    });
                    if (CoreUtils.objKeys(responseHeaderMap_1).length > 0) {
                        dependency.properties = dependency.properties || {};
                        dependency.properties.responseHeaders = {};
                        dependency.properties.responseHeaders = responseHeaderMap_1;
                    }
                }
            }
            if (_self._config.enableAjaxErrorStatusText && xhr.status >= 400) {
                dependency.properties = dependency.properties || {};
                dependency.properties.responseText = xhr.statusText + " - " + xhr.responseText;
            }
            _self.trackDependencyDataInternal(dependency);
            xhr.ajaxData = null;
        }
    };
    AjaxMonitor.prototype.getAjaxCorrelationContext = function (xhr) {
        try {
            var responseHeadersString = xhr.getAllResponseHeaders();
            if (responseHeadersString !== null) {
                var index = responseHeadersString.toLowerCase().indexOf(RequestHeaders.requestContextHeaderLowerCase);
                if (index !== -1) {
                    var responseHeader = xhr.getResponseHeader(RequestHeaders.requestContextHeader);
                    return CorrelationIdHelper.getCorrelationContext(responseHeader);
                }
            }
        }
        catch (e) {
            this.diagLog().throwInternal(LoggingSeverity.WARNING, _InternalMessageId.FailedMonitorAjaxGetCorrelationHeader, "Failed to get Request-Context correlation header as it may be not included in the response or not accessible.", {
                ajaxDiagnosticsMessage: AjaxMonitor.getFailedAjaxDiagnosticsMessage(xhr),
                exception: Util.dump(e)
            });
        }
    };
    AjaxMonitor.prototype.isFetchInstrumented = function (input) {
        return this._fetchInitialized && input[DisabledPropertyName] !== true;
    };
    AjaxMonitor.prototype.supportsFetch = function () {
        var result = true;
        var _global = getGlobal();
        if (!_global || _isNullOrUndefined(_global.Request) ||
            _isNullOrUndefined(_global.Request.prototype) ||
            _isNullOrUndefined(_global.fetch)) {
            result = false;
        }
        return result;
    };
    AjaxMonitor.prototype.createFetchRecord = function (input, init) {
        var _self = this;
        var traceID = (_self._context && _self._context.telemetryTrace && _self._context.telemetryTrace.traceID) || Util.generateW3CId();
        var spanID = Util.generateW3CId().substr(0, 16);
        var ajaxData = new ajaxRecord(traceID, spanID, _self.diagLog());
        ajaxData.requestSentTime = DateTimeUtils.Now();
        if (input instanceof Request) {
            ajaxData.requestUrl = input ? input.url : "";
        }
        else {
            ajaxData.requestUrl = input;
        }
        if (init && init.method) {
            ajaxData.method = init.method;
        }
        else if (input && input instanceof Request) {
            ajaxData.method = input.method;
        }
        else {
            ajaxData.method = "GET";
        }
        if (init && init.headers && _self._config.enableRequestHeaderTracking) {
            ajaxData.requestHeaders = init.headers;
        }
        else {
            ajaxData.requestHeaders = {};
        }
        return ajaxData;
    };
    AjaxMonitor.prototype.getFailedFetchDiagnosticsMessage = function (input) {
        var result = "";
        try {
            if (!_isNullOrUndefined(input)) {
                if (typeof (input) === "string") {
                    result += "(url: '" + input + "')";
                }
                else {
                    result += "(url: '" + input.url + "')";
                }
            }
        }
        catch (e) {
            this.diagLog().throwInternal(LoggingSeverity.CRITICAL, _InternalMessageId.FailedMonitorAjaxOpen, "Failed to grab failed fetch diagnostics message", { exception: Util.dump(e) });
        }
        return result;
    };
    AjaxMonitor.prototype.onFetchComplete = function (response, ajaxData) {
        var _self = this;
        if (!ajaxData) {
            return;
        }
        try {
            ajaxData.responseFinishedTime = DateTimeUtils.Now();
            ajaxData.CalculateMetrics();
            if (ajaxData.ajaxTotalDuration < 0) {
                _self.diagLog().throwInternal(LoggingSeverity.WARNING, _InternalMessageId.FailedMonitorAjaxDur, "Failed to calculate the duration of the fetch call, monitoring data for this fetch call won't be sent.", {
                    fetchDiagnosticsMessage: _self.getFailedFetchDiagnosticsMessage(response),
                    requestSentTime: ajaxData.requestSentTime,
                    responseFinishedTime: ajaxData.responseFinishedTime
                });
            }
            else {
                var dependency = {
                    id: "|" + ajaxData.traceID + "." + ajaxData.spanID,
                    target: ajaxData.getAbsoluteUrl(),
                    name: ajaxData.getPathName(),
                    type: "Fetch",
                    duration: ajaxData.ajaxTotalDuration,
                    success: response.status >= 200 && response.status < 400,
                    responseCode: response.status,
                    properties: { HttpMethod: ajaxData.method }
                };
                // enrich dependency target with correlation context from the server
                var correlationContext = _self.getFetchCorrelationContext(response);
                if (correlationContext) {
                    dependency.correlationContext = correlationContext;
                }
                if (_self._config.enableRequestHeaderTracking) {
                    if (CoreUtils.objKeys(ajaxData.requestHeaders).length > 0) {
                        dependency.properties = dependency.properties || {};
                        dependency.properties.requestHeaders = ajaxData.requestHeaders;
                    }
                }
                if (_self._config.enableResponseHeaderTracking) {
                    var responseHeaderMap_2 = {};
                    response.headers.forEach(function (value, name) {
                        responseHeaderMap_2[name] = value;
                    });
                    if (CoreUtils.objKeys(responseHeaderMap_2).length > 0) {
                        dependency.properties = dependency.properties || {};
                        dependency.properties.responseHeaders = responseHeaderMap_2;
                    }
                }
                _self.trackDependencyDataInternal(dependency);
            }
        }
        catch (e) {
            _self.diagLog().throwInternal(LoggingSeverity.WARNING, _InternalMessageId.FailedMonitorAjaxGetCorrelationHeader, "Failed to calculate the duration of the fetch call, monitoring data for this fetch call won't be sent.", {
                fetchDiagnosticsMessage: _self.getFailedFetchDiagnosticsMessage(response),
                exception: Util.dump(e)
            });
        }
    };
    AjaxMonitor.prototype.onFetchFailed = function (input, ajaxData, reason) {
        var _self = this;
        if (!ajaxData) {
            return;
        }
        try {
            ajaxData.responseFinishedTime = DateTimeUtils.Now();
            ajaxData.CalculateMetrics();
            if (ajaxData.ajaxTotalDuration < 0) {
                _self.diagLog().throwInternal(LoggingSeverity.WARNING, _InternalMessageId.FailedMonitorAjaxDur, "Failed to calculate the duration of the failed fetch call, monitoring data for this fetch call won't be sent.", {
                    fetchDiagnosticsMessage: _self.getFailedFetchDiagnosticsMessage(input),
                    requestSentTime: ajaxData.requestSentTime,
                    responseFinishedTime: ajaxData.responseFinishedTime
                });
            }
            else {
                var dependency = {
                    id: "|" + ajaxData.traceID + "." + ajaxData.spanID,
                    target: ajaxData.getAbsoluteUrl(),
                    name: ajaxData.getPathName(),
                    type: "Fetch",
                    duration: ajaxData.ajaxTotalDuration,
                    success: false,
                    responseCode: 0,
                    properties: { HttpMethod: ajaxData.method }
                };
                _self.trackDependencyDataInternal(dependency, { error: reason.message });
            }
        }
        catch (e) {
            _self.diagLog().throwInternal(LoggingSeverity.WARNING, _InternalMessageId.FailedMonitorAjaxGetCorrelationHeader, "Failed to calculate the duration of the failed fetch call, monitoring data for this fetch call won't be sent.", {
                fetchDiagnosticsMessage: _self.getFailedFetchDiagnosticsMessage(input),
                exception: Util.dump(e)
            });
        }
    };
    AjaxMonitor.prototype.getFetchCorrelationContext = function (response) {
        try {
            var responseHeader = response.headers.get(RequestHeaders.requestContextHeader);
            return CorrelationIdHelper.getCorrelationContext(responseHeader);
        }
        catch (e) {
            this.diagLog().throwInternal(LoggingSeverity.WARNING, _InternalMessageId.FailedMonitorAjaxGetCorrelationHeader, "Failed to get Request-Context correlation header as it may be not included in the response or not accessible.", {
                fetchDiagnosticsMessage: this.getFailedFetchDiagnosticsMessage(response),
                exception: Util.dump(e)
            });
        }
    };
    AjaxMonitor.identifier = "AjaxDependencyPlugin";
    return AjaxMonitor;
}(BaseTelemetryPlugin));
export { AjaxMonitor };
//# sourceMappingURL=ajax.js.map


//////////////////
// WEBPACK FOOTER
// ./~/@microsoft/applicationinsights-dependencies-js/dist-esm/ajax.js
// module id = 602
// module chunks = 42